import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { observable, action } from 'mobx';
import {
    Milestone, MilestoneStore,
    MILESTONE_TYPES, MILESTONE_STATUSES,
} from 'store/planning';
import { ProjectStore } from 'store/Project';

const TYPE_LABELS: Record<string, string> = {
    sprint: 'Sprint',
    feature: 'Feature',
    milestone: 'Milestone',
    phase: 'Phase',
};

const STATUS_LABELS: Record<string, string> = {
    planning: 'Planning',
    active: 'Active',
    completed: 'Completed',
    on_hold: 'On Hold',
};

interface Props {
    open: boolean;
    milestone: Milestone | null;
    milestoneStore: MilestoneStore;
    projectStore: ProjectStore;
    visibleProjects: any[];
    onClose: () => void;
    onSaved: () => void;
}

@observer
export class MilestoneForm extends Component<Props> {
    @observable name = '';
    @observable description = '';
    @observable type = 'milestone';
    @observable status = 'planning';
    @observable startDate = '';
    @observable deadline = '';
    @observable projectId: number | null = null;
    @observable saving = false;

    componentDidUpdate(prevProps: Props) {
        if (this.props.open && !prevProps.open) {
            this.populateFromMilestone();
        }
    }

    @action
    populateFromMilestone() {
        const { milestone, visibleProjects } = this.props;
        const defaultProjectId = visibleProjects.length > 0 ? visibleProjects[0].id : null;
        if (milestone) {
            this.name = milestone.name || '';
            this.description = milestone.description || '';
            this.type = milestone.type || 'milestone';
            this.status = milestone.status || 'planning';
            const sd = milestone.startDate as any;
            let startDateStr = '';
            if (sd) {
                startDateStr = sd.format ? sd.format('YYYY-MM-DD') : String(sd).substring(0, 10);
            }
            this.startDate = startDateStr;
            const dl = milestone.deadline as any;
            let deadlineStr = '';
            if (dl) {
                deadlineStr = dl.format ? dl.format('YYYY-MM-DD') : String(dl).substring(0, 10);
            }
            this.deadline = deadlineStr;
            const project = milestone.project as any;
            const projId = project?.id || project?.pk || null;
            this.projectId = projId || defaultProjectId;
        } else {
            this.name = '';
            this.description = '';
            this.type = 'milestone';
            this.status = 'planning';
            this.startDate = '';
            this.deadline = '';
            this.projectId = defaultProjectId;
        }
    }

    getProjectLabel = (p: any): string => {
        if (!p) return '';
        const company = p.company;
        const companyName = company ? (company.name || '') : '';
        const projectName = p.name || p.fullName || '';
        return companyName ? `${companyName} — ${projectName}` : projectName;
    };

    handleSave = async () => {
        if (!this.name.trim()) return;
        if (!this.projectId) return;

        this.saving = true;
        try {
            const { milestone, milestoneStore, onSaved } = this.props;
            const api = (milestoneStore as any).api;
            const payload: Record<string, any> = {
                name: this.name.trim(),
                description: this.description,
                type: this.type,
                status: this.status,
                project: this.projectId,
                order: milestone ? (milestone.order || 0) : milestoneStore.models.length,
            };
            if (this.startDate) payload.start_date = this.startDate;
            if (this.deadline) payload.deadline = this.deadline;

            if (milestone) {
                // PATCH existing — send all fields with correct snake_case names
                await api.patch(`subproject/${milestone.id}/`, payload);
            } else {
                // POST new
                await api.post('subproject/', payload);
            }
            await milestoneStore.fetch({ with: ['project'] });
            onSaved();
        } catch (e) {
            console.error('Failed to save milestone:', e);
        } finally {
            this.saving = false;
        }
    }

    handleDelete = async () => {
        const { milestone, milestoneStore, onSaved } = this.props;
        if (!milestone) return;
        if (!window.confirm(`Delete "${milestone.name}"?`)) return;
        this.saving = true;
        try {
            const api = (milestoneStore as any).api;
            await api.delete(`subproject/${milestone.id}/`);
            await milestoneStore.fetch({ with: ['project'] });
            onSaved();
        } catch (e) {
            console.error('Failed to delete milestone:', e);
        } finally {
            this.saving = false;
        }
    }

    render() {
        if (!this.props.open) return null;
        const { milestone, visibleProjects, onClose } = this.props;
        const isEditing = !!milestone;

        return (
            <div className="sp-modal-overlay" data-test-milestone-form onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
                <div className="sp-modal">
                    <div className="sp-modal-header">
                        <h3>{isEditing ? 'Edit Milestone' : 'New Milestone'}</h3>
                        <button className="sp-btn-icon" onClick={onClose} data-test-milestone-form-close>&times;</button>
                    </div>
                    <div className="sp-modal-body">
                        <div className="sp-form-group">
                            <label>Name</label>
                            <input
                                className="sp-form-input"
                                type="text"
                                value={this.name}
                                data-test-milestone-form-name
                                onChange={(e) => this.name = e.target.value}
                                placeholder="Enter milestone name..."
                            />
                        </div>
                        <div className="sp-form-group">
                            <label>Description</label>
                            <textarea
                                className="sp-form-textarea"
                                value={this.description}
                                data-test-milestone-form-description
                                onChange={(e) => this.description = e.target.value}
                                placeholder="Enter milestone description..."
                            />
                        </div>
                        <div className="sp-form-group">
                            <label>Project</label>
                            <select
                                className="sp-form-select"
                                value={this.projectId || ''}
                                data-test-milestone-form-project
                                onChange={(e) => this.projectId = e.target.value ? parseInt(e.target.value) : null}
                            >
                                <option value="">Select project...</option>
                                {visibleProjects.map((p: any) => (
                                    <option key={p.id} value={p.id}>{this.getProjectLabel(p)}</option>
                                ))}
                            </select>
                        </div>
                        <div className="sp-form-row">
                            <div className="sp-form-group">
                                <label>Type</label>
                                <select
                                    className="sp-form-select"
                                    value={this.type}
                                    data-test-milestone-form-type
                                    onChange={(e) => this.type = e.target.value}
                                >
                                    {MILESTONE_TYPES.map(t => (
                                        <option key={t} value={t}>{TYPE_LABELS[t] || t}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="sp-form-group">
                                <label>Status</label>
                                <select
                                    className="sp-form-select"
                                    value={this.status}
                                    data-test-milestone-form-status
                                    onChange={(e) => this.status = e.target.value}
                                >
                                    {MILESTONE_STATUSES.map(s => (
                                        <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="sp-form-row">
                            <div className="sp-form-group">
                                <label>Start Date</label>
                                <input
                                    className="sp-form-input"
                                    type="date"
                                    value={this.startDate}
                                    data-test-milestone-form-start-date
                                    onChange={(e) => this.startDate = e.target.value}
                                />
                            </div>
                            <div className="sp-form-group">
                                <label>Deadline</label>
                                <input
                                    className="sp-form-input"
                                    type="date"
                                    value={this.deadline}
                                    data-test-milestone-form-deadline
                                    onChange={(e) => this.deadline = e.target.value}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="sp-modal-footer">
                        {isEditing && (
                            <button
                                className="sp-btn sp-btn-danger"
                                style={{ marginRight: 'auto' }}
                                onClick={this.handleDelete}
                                disabled={this.saving}
                                data-test-milestone-form-delete
                            >
                                Delete
                            </button>
                        )}
                        <button className="sp-btn" onClick={onClose} disabled={this.saving}>Cancel</button>
                        <button
                            className="sp-btn sp-btn-primary"
                            onClick={this.handleSave}
                            disabled={this.saving || !this.name.trim() || !this.projectId}
                            data-test-milestone-form-save
                        >
                            {this.saving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}

export default MilestoneForm;
